/**
 * Media export utilities for CSK Canvas-based visualizations.
 *
 * Provides capture-to-blob helpers for:
 * - Single PNG frame
 * - Animated WebM via MediaRecorder (browser-native, no dependencies)
 * - Frame sequences for server-side encoding via ffmpeg
 *
 * Security invariants (CSK Neutralization Theorems):
 * - INV.ESTABLISH: all inputs validated at function boundaries before resource allocation
 * - INV.PRESERVE: MediaRecorder state machine guarded through all transitions
 * - RES.CONSUME / RES.RELEASE: stream tracks + timers cleaned up on all exit paths
 * - SEPARATE: filename sanitized to structurally exclude path traversal
 *
 * All functions operate on a standard HTMLCanvasElement reference.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { downloadBlob as sharedDownloadBlob } from "./downloadFile.js";

// ── INV.ESTABLISH: canvas dimension invariant checked at function boundaries ──

/** Validate that a canvas has non-zero drawable area. */
function assertCanvasDrawable(canvas: HTMLCanvasElement): void {
  if (canvas.width === 0 || canvas.height === 0) {
    throw new Error(
      `Canvas has zero dimensions (${canvas.width}×${canvas.height}). ` +
        "Ensure the canvas is mounted and sized before capturing.",
    );
  }
}

/** Capture a single frame as a PNG blob. */
export async function capturePng(canvas: HTMLCanvasElement): Promise<Blob> {
  assertCanvasDrawable(canvas); // INV.ESTABLISH
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas toBlob returned null"));
    }, "image/png");
  });
}

/** Default capture duration in seconds — short enough for inline previews. */
export const DEFAULT_CAPTURE_DURATION_S = 5;
/** Default WebM/MP4 frame rate. 30 fps is the de-facto demo cadence. */
export const DEFAULT_CAPTURE_FPS = 30;
/** Default bitrate (5 Mbps) — visually clean for canvas content without bloating exports. */
export const DEFAULT_CAPTURE_BITRATE_BPS = 5_000_000;
/**
 * How long the object URL stays alive before revocation. Generous on
 * purpose so a slow flush of a multi-MB capture does not race a fast
 * revoke and break the download.
 */
export const BLOB_URL_REVOKE_DELAY_MS = 60_000;

/** Options for animated capture. */
export interface AnimationCaptureOptions {
  /** Duration in seconds (default: {@link DEFAULT_CAPTURE_DURATION_S}). */
  duration?: number;
  /** Target frames per second (default: {@link DEFAULT_CAPTURE_FPS}). */
  fps?: number;
  /** Video bitrate in bits/second (default: {@link DEFAULT_CAPTURE_BITRATE_BPS}). */
  bitrate?: number;
}

/**
 * Capture an animated WebM from a canvas using the MediaRecorder API.
 *
 * The canvas must be actively animating (drawing new frames via RAF)
 * during the capture window. Returns a WebM blob after `duration` seconds.
 *
 * Browser support: Chrome 49+, Firefox 68+, Edge 79+.
 */
export async function captureWebM(
  canvas: HTMLCanvasElement,
  options: AnimationCaptureOptions = {},
): Promise<Blob> {
  const {
    duration = DEFAULT_CAPTURE_DURATION_S,
    fps = DEFAULT_CAPTURE_FPS,
    bitrate = DEFAULT_CAPTURE_BITRATE_BPS,
  } = options;

  // INV.ESTABLISH: validate all parameters at boundary before allocating resources
  assertCanvasDrawable(canvas);
  if (duration <= 0 || !Number.isFinite(duration)) {
    throw new Error(`Invalid capture duration: ${duration}. Must be a positive number.`);
  }
  if (fps <= 0 || !Number.isFinite(fps)) {
    throw new Error(`Invalid fps: ${fps}. Must be a positive number.`);
  }
  if (bitrate <= 0 || !Number.isFinite(bitrate)) {
    throw new Error(`Invalid bitrate: ${bitrate}. Must be a positive number.`);
  }

  // SEPARATE: feature detection before touching browser APIs
  if (typeof MediaRecorder === "undefined") {
    throw new Error(
      "MediaRecorder API is not available in this browser. " +
        "WebM capture requires Chrome 49+, Firefox 68+, or Edge 79+.",
    );
  }

  // SEPARATE: probe supported codec in priority order — VP9 > VP8 > plain webm.
  // Surface a clear error if no supported type is available, rather than letting
  // the MediaRecorder constructor throw an opaque DOMException.
  const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
    ? "video/webm;codecs=vp9"
    : MediaRecorder.isTypeSupported("video/webm;codecs=vp8")
      ? "video/webm;codecs=vp8"
      : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : null;

  if (mimeType === null) {
    throw new Error(
      "No supported WebM codec available in this browser (VP9, VP8, and plain WebM " +
        "all failed isTypeSupported). Try Chrome 49+, Firefox 68+, or Edge 79+.",
    );
  }

  // RES.CONSUME: acquire the capture stream.
  // RES.RELEASE: guaranteed via the cleanup helper on all exit paths below.
  const stream = canvas.captureStream(fps);

  // Shared cleanup — stops all stream tracks to release the capture resource.
  const releaseStream = (): void => {
    stream.getTracks().forEach((t) => {
      t.stop();
    });
  };

  return new Promise<Blob>((resolve, reject) => {
    // RES.CONSUME: construct the recorder.
    // Wrap in try/catch so a DOMException from an unsupported option still
    // releases the stream (INV.PRESERVE + RES.RELEASE).
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: bitrate,
      });
    } catch (err) {
      releaseStream(); // RES.RELEASE
      reject(
        err instanceof Error ? err : new Error(`MediaRecorder construction failed: ${String(err)}`),
      );
      return;
    }

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e): void => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // INV.PRESERVE: timer ID tracked so it can be cleared on all exit paths
    let stopTimerId: ReturnType<typeof setTimeout> | undefined;

    recorder.onerror = (): void => {
      // RES.RELEASE: clean up timer + stream tracks on error
      if (stopTimerId !== undefined) clearTimeout(stopTimerId);
      releaseStream();
      reject(new Error("MediaRecorder error"));
    };
    recorder.onstop = (): void => {
      // RES.RELEASE: clean up timer + stream tracks on completion
      if (stopTimerId !== undefined) clearTimeout(stopTimerId);
      releaseStream();
      resolve(new Blob(chunks, { type: mimeType }));
    };

    // Wrap recorder.start() — if it throws synchronously the stream must be released.
    try {
      recorder.start(100); // collect data every 100ms
    } catch (err) {
      releaseStream(); // RES.RELEASE
      reject(err instanceof Error ? err : new Error(`MediaRecorder start failed: ${String(err)}`));
      return;
    }

    stopTimerId = setTimeout(() => {
      // INV.PRESERVE: guard state transition — only stop if still recording
      if (recorder.state === "recording") recorder.stop();
    }, duration * 1000);
  });
}

/**
 * Capture a sequence of PNG frames from a canvas.
 *
 * Captures `count` frames at `interval` ms apart. Useful as input for
 * server-side GIF/MP4 encoding via ffmpeg or gifenc.
 */
export async function captureFrameSequence(
  canvas: HTMLCanvasElement,
  options: { count?: number; intervalMs?: number } = {},
): Promise<Blob[]> {
  const { count = 60, intervalMs = 33 } = options;

  // INV.ESTABLISH: validate parameters before allocating frame buffer
  assertCanvasDrawable(canvas);
  if (count <= 0 || !Number.isInteger(count)) {
    throw new Error(`Invalid frame count: ${count}. Must be a positive integer.`);
  }
  if (intervalMs <= 0 || !Number.isFinite(intervalMs)) {
    throw new Error(`Invalid interval: ${intervalMs}ms. Must be a positive number.`);
  }

  const frames: Blob[] = [];

  for (let i = 0; i < count; i++) {
    const blob = await capturePng(canvas);
    frames.push(blob);
    if (i < count - 1) {
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }

  return frames;
}

/**
 * Trigger a browser download for a blob.
 *
 * Delegates to the shared `downloadBlob` from `@csk/demo-ui/utils`
 * which applies the same filename sanitisation (whitelist of safe
 * characters, consecutive dots collapsed, fallback to `"download"`)
 * and the deferred blob-URL revocation policy.
 */
export const downloadBlob = sharedDownloadBlob;
